<script setup lang="ts">
import { ref, onMounted, onUnmounted, inject } from "vue";
import type { Ref } from "vue";
import { APIURL } from "@/defaults";
import type { TickerIntradayChange } from "@/interfaces/interfaces.js";
import ColoredNumber from "@/components/ColoredNumber.vue";
import { parseDateString } from "@/helpers/dates";

interface Ticker {
    name: string;
    id: string; // Yahoo Finance ticker symbol or similar identifier
}

const tickers: Ticker[] = [
    { name: "NASDAQ-100", id: "^NDX" },
    { name: "S&P500", id: "^GSPC" },
    { name: "STOXX Europe 600", id: "^STOXX" },
    { name: "Hang Seng", id: "^HSI" },
    { name: "EUR/USD", id: "EURUSD=X" },
];

// Store calculated intraday changes for each ticker
const tickerIntradayChanges: Ref<Record<string, number | null>> = ref({});

let intervalId: any;
const wsPubSub = inject('wsPubSub') as { subscribe: (cb: any) => void, unsubscribe: (cb: any) => void, publish: (msg: any) => void } | undefined;

const handleRealtimeUpdate = (message: any) => {
    if (message.type === "realtime_update" && message.ticker && typeof message.intraday_change === 'number') {
        if (tickers.some(t => t.id === message.ticker)) {
            tickerIntradayChanges.value[message.ticker] = message.intraday_change;
        }
    }
};

const fetchTickerData = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today to the start of the day

    for (const ticker of tickers) {
        // User simplified URL fetching in their local changes, always using /index/
        const url = `${APIURL}/index/${ticker.id}/realtime?disable_currency_adjustment=True`;

        try {
            const response = await fetch(url, { credentials: "include" });
            if (!response.ok) {
                console.error(`Failed to fetch chart data for ${ticker.id}: ${response.statusText} (status: ${response.status})`);
                tickerIntradayChanges.value[ticker.id] = null; // Error state
                continue;
            }

            const data: TickerIntradayChange = await response.json();

            // Directly use the intraday_change from the API response
            tickerIntradayChanges.value[ticker.id] = data.intraday_change || 0;

        } catch (error) { // Network errors, JSON parse errors etc.
            console.error(`Error processing chart for ${ticker.id}:`, error);
            tickerIntradayChanges.value[ticker.id] = null; // Error state
        }
    }
};

onMounted(async () => {
    await fetchTickerData(); // Initial fetch
    intervalId = setInterval(fetchTickerData, 5 * 60 * 1000); // Fetch every 5 minutes

    if (wsPubSub) {
        wsPubSub.subscribe(handleRealtimeUpdate);
    }
});

onUnmounted(() => {
    if (intervalId) {
        clearInterval(intervalId);
    }
    if (wsPubSub) {
        wsPubSub.unsubscribe(handleRealtimeUpdate);
    }
});

const getPriceChange = (tickerId: string): number | null => {
    // Returns the pre-calculated change, or null if not available/error
    return tickerIntradayChanges.value[tickerId] ?? null;
};

</script>

<template>
    <div class="ticker-bar">
        <div v-for="ticker in tickers" :key="ticker.id" class="ticker-bar-item">
            <p>{{ ticker.name }}</p>
            <p class="live-change">
                <ColoredNumber v-if="getPriceChange(ticker.id) !== null" :value="getPriceChange(ticker.id)" :percent="true" />
                <span v-else>…</span>
            </p>
        </div>
    </div>
</template>

<style scoped>
.ticker-bar {
    height: 36px;
    background: #333;
    color: #fff;
    display: flex;
    justify-content: space-around;
    font-size: 12px;
    line-height: 36px;
    padding: 0 20px;
}
.ticker-bar-item {
    display: flex;
    gap: 10px;
}
</style>
